import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, ShoppingCart, Settings, LogOut, Plus, Pencil, Trash2, X, Camera, ImagePlus, Ticket, Star, Globe, Copy, Loader2, MessageSquare } from "lucide-react";
import { api, formatApiError, formatBRL, resolveImageUrl } from "../lib/api";
import { Logo } from "../components/store/Header";
import AdminReviews from "./admin/AdminReviews";
import AdminSite from "./admin/AdminSite";
import AdminMessages from "./admin/AdminMessages";

const EMPTY_FORM = { name: "", description: "", price: "", category: "Acessórios", brand: "", status: "disponivel", image_url: "", photos: [], badge: "", active: true, stock: "", promo_price: "" };

function ProductForm({ initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Foto enviada!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const addGalleryPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, photos: [...(f.photos || []), data.url] }));
      toast.success("Foto adicionada à galeria!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryPhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));
  const moveGalleryPhoto = (i, dir) =>
    setForm((f) => {
      const photos = [...(f.photos || [])];
      const target = i + dir;
      if (target < 0 || target >= photos.length) return f;
      [photos[i], photos[target]] = [photos[target], photos[i]];
      return { ...f, photos };
    });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="product-form-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[var(--site-bg)] w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-[#E8E2D9] p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-xl">{form.id ? "Editar Produto" : "Novo Produto"}</h2>
          <button type="button" onClick={onClose} data-testid="product-form-close" className="p-2 rounded-full hover:bg-[#F2EDE4] transition-colors" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave({ ...form, price: parseFloat(String(form.price).replace(",", ".")) || 0, promo_price: form.promo_price !== "" ? parseFloat(String(form.promo_price).replace(",", ".")) : null, stock: form.stock !== "" ? parseInt(form.stock, 10) : null });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Nome *</label>
            <input required value={form.name} onChange={set("name")} data-testid="product-name-input"
              className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Preço (R$) *</label>
              <input required inputMode="decimal" value={form.price} onChange={set("price")} placeholder="99,90" data-testid="product-price-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Categoria</label>
              <input value={form.category} onChange={set("category")} placeholder="Acessórios" data-testid="product-category-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            </div>
          </div>
          <div>
            <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Descrição</label>
            <textarea value={form.description} onChange={set("description")} rows={2} data-testid="product-description-input"
              className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors resize-none" />
          </div>
          <div>
            <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Foto do Produto</label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-[#EDE6DB] border border-[#E8E2D9] shrink-0 flex items-center justify-center">
                {form.image_url ? (
                  <img src={resolveImageUrl(form.image_url)} alt="Foto do produto" className="w-full h-full object-cover" data-testid="product-photo-preview" />
                ) : (
                  <ImagePlus className="w-6 h-6 text-[#A89E94]" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid="product-camera-input" />
                <input ref={galleryRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="product-gallery-input" />
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="product-camera-button"
                    className="inline-flex items-center gap-2 bg-[#1A1816] text-white px-4 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium hover:bg-[var(--brand)] transition-colors disabled:opacity-60">
                    <Camera className="w-4 h-4" /> Tirar Foto
                  </button>
                  <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploading} data-testid="product-gallery-button"
                    className="inline-flex items-center gap-2 border border-[#D5CDC2] px-4 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium hover:border-[#1A1816] transition-colors disabled:opacity-60">
                    <ImagePlus className="w-4 h-4" /> Galeria
                  </button>
                </div>
                <p className="text-[11px] text-[#6B645C]">{uploading ? "Enviando foto..." : "Ou cole o link de uma foto abaixo"}</p>
              </div>
            </div>
            <input value={form.image_url} onChange={set("image_url")} placeholder="https://... (opcional)" data-testid="product-image-input"
              className="mt-2 w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
          </div>
          <div>
            <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Galeria de Fotos (página do produto)</label>
            <div className="flex flex-wrap gap-2 items-center">
              {(form.photos || []).map((p, i) => (
                <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-[#E8E2D9] group/gal" data-testid={`gallery-photo-${i}`}>
                  <img src={resolveImageUrl(p)} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/gal:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: p }))} data-testid={`gallery-main-${i}`} className="text-white" aria-label="Definir como foto principal" title="Foto principal">
                      <Star className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveGalleryPhoto(i, -1)} data-testid={`gallery-left-${i}`} className="text-white text-xs px-1" aria-label="Mover para esquerda">←</button>
                    <button type="button" onClick={() => removeGalleryPhoto(i)} data-testid={`gallery-remove-${i}`} className="text-white" aria-label="Remover foto">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveGalleryPhoto(i, 1)} data-testid={`gallery-right-${i}`} className="text-white text-xs px-1" aria-label="Mover para direita">→</button>
                  </div>
                </div>
              ))}
              <label className="w-16 h-16 rounded-xl border border-dashed border-[#D5CDC2] flex items-center justify-center cursor-pointer hover:border-[var(--brand)] transition-colors" data-testid="gallery-add-photo">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5 text-[#6B645C]" />}
                <input type="file" accept="image/*" onChange={addGalleryPhoto} className="hidden" />
              </label>
            </div>
            <p className="text-[11px] text-[#6B645C] mt-2">Passe o mouse/toque na foto: ★ define a principal, ← → mudam a ordem, lixeira exclui.</p>
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Etiqueta</label>
              <input value={form.badge} onChange={set("badge")} placeholder="Ex: Novo" data-testid="product-badge-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            </div>
            <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="product-active-checkbox" className="w-4 h-4 accent-[var(--brand)]" />
              <span className="text-sm">Visível na loja</span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Preço Promocional (R$)</label>
              <input inputMode="decimal" value={form.promo_price ?? ""} onChange={set("promo_price")} placeholder="Opcional" data-testid="product-promo-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Estoque</label>
              <input inputMode="numeric" value={form.stock ?? ""} onChange={set("stock")} placeholder="Vazio = sem controle" data-testid="product-stock-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Marca</label>
              <input value={form.brand ?? ""} onChange={set("brand")} placeholder="Ex: Apple, Samsung" data-testid="product-brand-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Disponibilidade</label>
              <select value={form.status ?? "disponivel"} onChange={set("status")} data-testid="product-status-select"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors">
                <option value="disponivel">Disponível</option>
                <option value="indisponivel">Indisponível</option>
                <option value="sob_encomenda">Sob encomenda</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="product-save-button"
            className="w-full bg-[#1A1816] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors disabled:opacity-60">
            {saving ? "Salvando..." : "Salvar Produto"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("produtos");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("");
  const [settings, setSettings] = useState({ store_name: "", whatsapp_number: "", address: "", logo_url: "", reviews_enabled: true });
  const [fees, setFees] = useState({ pix: "", debito: "", credito_avista: "", parcelas: {} });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ id: "", code: "", type: "percent", value: "", active: true });

  const loadCoupons = () => api.get("/coupons").then((res) => setCoupons(res.data)).catch(() => {});

  const paidCountRef = useRef(null);
  const msgCountRef = useRef(null);

  // Aviso de venda e novas mensagens: verifica a cada 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
        const paid = data.filter((o) => o.status === "pago").length;
        if (paidCountRef.current !== null && paid > paidCountRef.current) {
          toast.success("Nova venda aprovada! Confira na aba Pedidos.", { duration: 8000 });
        }
        paidCountRef.current = paid;
        const { data: msgs } = await api.get("/admin/messages");
        const novas = msgs.filter((m) => m.status === "nova").length;
        if (msgCountRef.current !== null && novas > msgCountRef.current) {
          toast.success("Nova mensagem de cliente! Confira na aba Atendimento.", { duration: 8000 });
        }
        msgCountRef.current = novas;
      } catch {
        // silencioso
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const loadProducts = () => api.get("/products/all").then((res) => setProducts(res.data));
  const loadOrders = () => api.get("/orders").then((res) => setOrders(res.data));

  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => {
        loadProducts().catch(() => {});
        loadOrders().catch(() => {});
        loadCoupons();
        api.get("/settings").then((res) => setSettings(res.data)).catch(() => {});
        api.get("/settings/fees").then((res) => {
          const f = res.data;
          setFees({
            pix: String(f.pix),
            debito: String(f.debito),
            credito_avista: String(f.credito_avista),
            parcelas: Object.fromEntries(Object.entries(f.parcelas || {}).map(([k, v]) => [k, String(v)])),
          });
        }).catch(() => {});
      })
      .catch(() => {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProduct = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/products/${form.id}`, form);
        toast.success("Produto atualizado!");
      } else {
        await api.post("/products", form);
        toast.success("Produto adicionado à loja!");
      }
      setEditing(null);
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Remover este produto da loja?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produto removido.");
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicateProduct = async (id) => {
    try {
      await api.post(`/products/${id}/duplicate`);
      toast.success("Produto duplicado! Edite a cópia.");
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleReviewsEnabled = async () => {
    const next = { ...settings, reviews_enabled: !settings.reviews_enabled };
    try {
      await api.put("/settings", next);
      setSettings(next);
      toast.success(next.reviews_enabled ? "Recebimento de avaliações ativado!" : "Recebimento de avaliações desativado.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveCoupon = async (e) => {
    e.preventDefault();
    const payload = {
      code: couponForm.code.trim().toUpperCase(),
      type: couponForm.type,
      value: parseFloat(String(couponForm.value).replace(",", ".")) || 0,
      active: couponForm.active,
    };
    try {
      if (couponForm.id) {
        await api.put(`/coupons/${couponForm.id}`, payload);
        toast.success("Cupom atualizado!");
      } else {
        await api.post("/coupons", payload);
        toast.success("Cupom criado!");
      }
      setCouponForm({ id: "", code: "", type: "percent", value: "", active: true });
      loadCoupons();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Apagar este cupom?")) return;
    try {
      await api.delete(`/coupons/${id}`);
      toast.success("Cupom removido.");
      loadCoupons();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveFees = async (e) => {
    e.preventDefault();
    const parcelas = {};
    for (const [k, v] of Object.entries(fees.parcelas)) {
      const num = parseFloat(String(v).replace(",", "."));
      if (v !== "" && !isNaN(num)) parcelas[k] = num;
    }
    try {
      await api.put("/settings/fees", {
        pix: parseFloat(String(fees.pix).replace(",", ".")) || 0,
        debito: parseFloat(String(fees.debito).replace(",", ".")) || 0,
        credito_avista: parseFloat(String(fees.credito_avista).replace(",", ".")) || 0,
        parcelas,
      });
      toast.success("Taxas atualizadas! O simulador já usa os novos valores.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSettingsData = async (data) => {
    try {
      await api.put("/settings", data);
      setSettings(data);
      toast.success("Configurações salvas!");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    saveSettingsData(settings);
  };

  const ORDER_STATUSES = ["novo", "pago", "em separação", "a caminho", "entregue", "atendido", "cancelado"];
  const ORDER_STATUS_STYLE = {
    novo: "bg-[var(--brand)] text-white border-[var(--brand)]",
    pago: "bg-green-600 text-white border-green-600",
    "em separação": "bg-blue-50 text-blue-700 border-blue-200",
    "a caminho": "bg-amber-50 text-amber-700 border-amber-200",
    entregue: "bg-green-50 text-green-700 border-green-200",
    atendido: "bg-green-50 text-green-700 border-green-200",
    cancelado: "bg-red-50 text-red-600 border-red-200",
  };

  const setOrderStatus = async (order, status) => {
    try {
      await api.patch(`/orders/${order.id}/status`, { status });
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const TABS = [
    { key: "produtos", label: "Produtos", icon: Package },
    { key: "pedidos", label: "Pedidos", icon: ShoppingCart },
    { key: "atendimento", label: "Atendimento", icon: MessageSquare },
    { key: "avaliacoes", label: "Avaliações", icon: Star },
    { key: "cupons", label: "Cupons", icon: Ticket },
    { key: "site", label: "Site", icon: Globe },
    { key: "config", label: "Configurações", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[var(--site-bg)]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[var(--site-bg)]/90 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 font-display font-semibold">
            <img src="/logo.png" alt="Dra.Cell" className="h-8 w-auto" />
            <span className="text-sm text-[#5A534C]">Painel da Loja</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-store-link" className="hidden sm:inline font-mono text-xs uppercase tracking-wider text-[#5A534C] hover:text-[var(--brand)] transition-colors">
              Ver Loja
            </Link>
            <button type="button" onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-[#5A534C] hover:text-[var(--brand)] transition-colors">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 pb-3">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-display text-xs uppercase tracking-wider font-medium transition-colors ${
                tab === t.key ? "bg-[#1A1816] text-white" : "text-[#5A534C] hover:bg-[#F2EDE4]"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {tab === "produtos" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-bold text-2xl tracking-tight">Produtos ({products.length})</h1>
              <button type="button" onClick={() => setEditing({ ...EMPTY_FORM })} data-testid="add-product-button"
                className="inline-flex items-center gap-2 bg-[var(--brand)] text-white px-5 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium hover:bg-[var(--brand-dark)] transition-colors">
                <Plus className="w-4 h-4" /> Novo Produto
              </button>
            </div>
            {products.length === 0 ? (
              <p className="text-sm text-[#6B645C]" data-testid="products-empty">Nenhum produto cadastrado ainda.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-products-list">
                {products.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-4 flex gap-4" data-testid={`admin-product-${p.id}`}>
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#EDE6DB] shrink-0">
                      {p.image_url && <img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-semibold text-sm truncate">{p.name}</p>
                      <p className="font-mono text-xs text-[#5A534C]">
                        {formatBRL(p.price)}
                        {p.promo_price ? ` → ${formatBRL(p.promo_price)}` : ""} • {p.category}
                        {p.stock !== null && p.stock !== undefined ? ` • estoque: ${p.stock}` : ""}
                      </p>
                      <p className={`text-[11px] font-mono uppercase mt-1 ${p.active ? "text-green-700" : "text-[#A89E94]"}`}>
                        {p.active ? "Visível" : "Oculto"}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <button type="button" onClick={() => setEditing({ ...p, stock: p.stock ?? "", promo_price: p.promo_price ?? "" })} data-testid={`edit-product-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-[#1A1816] transition-colors">
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </button>
                        <button type="button" onClick={() => duplicateProduct(p.id)} data-testid={`duplicate-product-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-[#1A1816] transition-colors">
                          <Copy className="w-3.5 h-3.5" /> Duplicar
                        </button>
                        <button type="button" onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-red-600 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" /> Remover
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "pedidos" && (
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight mb-4">Pedidos ({orders.length})</h1>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="orders-filters">
              {["", "novo", "pago", "em separação", "a caminho", "entregue", "cancelado"].map((s) => (
                <button key={s || "todos"} type="button" onClick={() => setOrderFilter(s)} data-testid={`order-filter-${s || "todos"}`}
                  className={`px-3 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-wider border transition-colors ${
                    orderFilter === s ? "bg-[#1A1816] text-white border-[#1A1816]" : "border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"
                  }`}>
                  {s || "Todos"}
                </button>
              ))}
            </div>
            {orders.filter((o) => !orderFilter || o.status === orderFilter).length === 0 ? (
              <p className="text-sm text-[#6B645C]" data-testid="orders-empty">Nenhum pedido {orderFilter ? `com status "${orderFilter}"` : "recebido"} ainda.</p>
            ) : (
              <div className="space-y-4" data-testid="admin-orders-list">
                {orders.filter((o) => !orderFilter || o.status === orderFilter).map((o) => (
                  <div key={o.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid={`admin-order-${o.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div>
                        <p className="font-display font-semibold text-sm">
                          {o.customer_name || "Cliente"} <span className="font-mono text-xs text-[#6B645C]">• {new Date(o.created_at).toLocaleString("pt-BR")}</span>
                        </p>
                        {o.customer_email && <p className="font-mono text-[11px] text-[#6B645C]">{o.customer_email}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {o.payment_method && (
                          <span className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#D5CDC2] text-[#5A534C]">
                            {o.payment_method}
                          </span>
                        )}
                        <select value={o.status} onChange={(e) => setOrderStatus(o, e.target.value)} data-testid={`order-status-${o.id}`}
                          className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${ORDER_STATUS_STYLE[o.status] || ORDER_STATUS_STYLE.novo}`}>
                          {ORDER_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <ul className="text-sm text-[#5A534C] space-y-1">
                      {o.items.map((i, idx) => (
                        <li key={idx}>{i.qty}x {i.name} — {formatBRL(i.price * i.qty)}</li>
                      ))}
                    </ul>
                    <div className="text-sm text-[#5A534C] mt-3 space-y-1">
                      {o.subtotal != null && <p>Subtotal: {formatBRL(o.subtotal)}</p>}
                      {o.shipping && (
                        <p>Frete ({o.shipping.name}{o.shipping.company ? ` · ${o.shipping.company}` : ""}): {formatBRL(o.shipping.price)}{o.cep ? ` — CEP ${o.cep}` : ""}</p>
                      )}
                      {o.discount > 0 && <p className="text-green-700">Cupom {o.coupon_code}: -{formatBRL(o.discount)}</p>}
                    </div>
                    <p className="font-display font-bold text-sm mt-3">Total: {formatBRL(o.total)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "avaliacoes" && <AdminReviews settings={settings} onToggleEnabled={toggleReviewsEnabled} />}

        {tab === "atendimento" && <AdminMessages settings={settings} onSaveSettings={saveSettingsData} />}

        {tab === "site" && <AdminSite />}

        {tab === "cupons" && (
          <div className="max-w-2xl">
            <h1 className="font-display font-bold text-2xl tracking-tight mb-6">Cupons de Desconto ({coupons.length})</h1>
            <form onSubmit={saveCoupon} className="bg-white rounded-3xl border border-[#E8E2D9] p-6 mb-6 space-y-4" data-testid="coupon-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Código *</label>
                  <input required value={couponForm.code} onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                    placeholder="BEMVINDO10" data-testid="coupon-code-input"
                    className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm uppercase focus:outline-none focus:border-[var(--brand)] transition-colors" />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Tipo</label>
                  <select value={couponForm.type} onChange={(e) => setCouponForm((f) => ({ ...f, type: e.target.value }))} data-testid="coupon-type-select"
                    className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors">
                    <option value="percent">Porcentagem (%)</option>
                    <option value="fixed">Valor fixo (R$)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">
                    {couponForm.type === "percent" ? "Desconto (%)" : "Desconto (R$)"} *
                  </label>
                  <input required inputMode="decimal" value={couponForm.value} onChange={(e) => setCouponForm((f) => ({ ...f, value: e.target.value.replace(/[^\d.,]/g, "") }))}
                    placeholder={couponForm.type === "percent" ? "10" : "15,00"} data-testid="coupon-value-input"
                    className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                </div>
                <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
                  <input type="checkbox" checked={couponForm.active} onChange={(e) => setCouponForm((f) => ({ ...f, active: e.target.checked }))} data-testid="coupon-active-checkbox" className="w-4 h-4 accent-[var(--brand)]" />
                  <span className="text-sm">Cupom ativo</span>
                </label>
              </div>
              <div className="flex gap-2">
                <button type="submit" data-testid="coupon-save-button"
                  className="flex-1 bg-[#1A1816] text-white py-3 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors">
                  {couponForm.id ? "Salvar Alterações" : "Criar Cupom"}
                </button>
                {couponForm.id && (
                  <button type="button" onClick={() => setCouponForm({ id: "", code: "", type: "percent", value: "", active: true })} data-testid="coupon-cancel-edit"
                    className="px-5 py-3 rounded-full border border-[#D5CDC2] font-display text-sm hover:border-[#1A1816] transition-colors">
                    Cancelar
                  </button>
                )}
              </div>
            </form>

            {coupons.length === 0 ? (
              <p className="text-sm text-[#6B645C]" data-testid="coupons-empty">Nenhum cupom criado ainda.</p>
            ) : (
              <div className="space-y-3" data-testid="coupons-list">
                {coupons.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-4 flex items-center justify-between gap-3" data-testid={`coupon-${c.id}`}>
                    <div>
                      <p className="font-display font-semibold text-sm">{c.code}</p>
                      <p className="font-mono text-xs text-[#5A534C]">
                        {c.type === "percent" ? `${c.value}% de desconto` : `${formatBRL(c.value)} de desconto`}
                        {" • "}
                        <span className={c.active ? "text-green-700" : "text-[#A89E94]"}>{c.active ? "ativo" : "inativo"}</span>
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button type="button" onClick={() => setCouponForm({ id: c.id, code: c.code, type: c.type, value: String(c.value), active: c.active })} data-testid={`edit-coupon-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-[#1A1816] transition-colors">
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                      <button type="button" onClick={() => deleteCoupon(c.id)} data-testid={`delete-coupon-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-red-600 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Apagar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "config" && (
          <div className="max-w-md space-y-8">
            <div>
            <h1 className="font-display font-bold text-2xl tracking-tight mb-6">Configurações</h1>
            <form onSubmit={saveSettings} className="bg-white rounded-3xl border border-[#E8E2D9] p-8 space-y-4" data-testid="settings-form">
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Nome da Loja</label>
                <input value={settings.store_name} onChange={(e) => setSettings((s) => ({ ...s, store_name: e.target.value }))}
                  data-testid="settings-store-name-input"
                  className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">WhatsApp (com DDD e código do país)</label>
                <input value={settings.whatsapp_number} onChange={(e) => setSettings((s) => ({ ...s, whatsapp_number: e.target.value }))}
                  placeholder="5511999999999" data-testid="settings-whatsapp-input"
                  className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                <p className="text-[11px] text-[#6B645C] mt-2">Os pedidos da sacola chegam neste número.</p>
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Endereço da Loja (para o botão "Como Chegar")</label>
                <input value={settings.address} onChange={(e) => setSettings((s) => ({ ...s, address: e.target.value }))}
                  placeholder="Rua, número, bairro, cidade - UF" data-testid="settings-address-input"
                  className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                <p className="text-[11px] text-[#6B645C] mt-2">Coloque o número e o bairro para o mapa abrir no ponto certo.</p>
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5A534C] mb-2">Logo da Loja</label>
                <div className="flex items-center gap-3">
                  <img src={settings.logo_url ? resolveImageUrl(settings.logo_url) : "/logo.png"} alt="Logo atual" className="h-10 w-auto bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl p-1" data-testid="settings-logo-preview" />
                  <label className="inline-flex items-center gap-2 border border-[#D5CDC2] px-4 py-2 rounded-full font-display text-xs font-medium hover:border-[#1A1816] transition-colors cursor-pointer">
                    <Camera className="w-4 h-4" /> Trocar logo
                    <input type="file" accept="image/*" className="hidden" data-testid="settings-logo-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const { data } = await api.post("/upload", fd);
                          setSettings((s) => ({ ...s, logo_url: data.url }));
                          toast.success("Logo enviada! Clique em Salvar Configurações.");
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }} />
                  </label>
                </div>
                <p className="text-[11px] text-[#6B645C] mt-2">A logo também vira o ícone da aba do navegador (favicon).</p>
              </div>
              <button type="submit" data-testid="settings-save-button"
                className="w-full bg-[#1A1816] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors">
                Salvar Configurações
              </button>
            </form>
            </div>

            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-2">Taxas da Maquininha (%)</h2>
              <p className="text-sm text-[#6B645C] mb-4">
                Mesmas taxas do app do Mercado Pago (Seu Negócio → Simulador). Deixe em branco as parcelas que não quiser exibir no simulador.
              </p>
              <form onSubmit={saveFees} className="bg-white rounded-3xl border border-[#E8E2D9] p-8 space-y-4" data-testid="fees-form">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-2">Pix</label>
                    <input inputMode="decimal" value={fees.pix} onChange={(e) => setFees((f) => ({ ...f, pix: e.target.value.replace(/[^\d.,]/g, "") }))} data-testid="fee-pix-input"
                      className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                  </div>
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-2">Débito</label>
                    <input inputMode="decimal" value={fees.debito} onChange={(e) => setFees((f) => ({ ...f, debito: e.target.value.replace(/[^\d.,]/g, "") }))} data-testid="fee-debito-input"
                      className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                  </div>
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-2">À vista</label>
                    <input inputMode="decimal" value={fees.credito_avista} onChange={(e) => setFees((f) => ({ ...f, credito_avista: e.target.value.replace(/[^\d.,]/g, "") }))} data-testid="fee-avista-input"
                      className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                  </div>
                </div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-[#5A534C] pt-2">Parcelado (juros do cliente)</p>
                <div className="grid grid-cols-4 gap-3">
                  {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (
                    <div key={n}>
                      <label className="block font-mono text-[11px] text-[#6B645C] mb-1">{n}x</label>
                      <input inputMode="decimal" value={fees.parcelas[String(n)] || ""} data-testid={`fee-${n}x-input`}
                        onChange={(e) => setFees((f) => ({ ...f, parcelas: { ...f.parcelas, [String(n)]: e.target.value.replace(/[^\d.,]/g, "") } }))}
                        className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-2 py-2 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                    </div>
                  ))}
                </div>
                <button type="submit" data-testid="fees-save-button"
                  className="w-full bg-[#1A1816] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors">
                  Salvar Taxas
                </button>
              </form>
            </div>
          </div>
        )}
      </main>

      {editing && <ProductForm initial={editing} onSave={saveProduct} onClose={() => setEditing(null)} saving={saving} />}
    </div>
  );
}
